import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Logger } from '../../utils/logger';

const log = Logger.create('MathRenderer');

/**
 * MathRenderer - Renders text with inline ($...$) and block ($$...$$) LaTeX
 * Uses KaTeX for rendering if available, falls back to styled text
 */

// Import KaTeX directly - Vite will bundle it
import katex from 'katex';
import 'katex/dist/katex.min.css';

/**
 * Parse text and extract LaTeX parts
 * Also auto-wraps LaTeX commands if not already in delimiters
 */
function parseLatex(text) {
  if (!text) return [{ type: 'text', content: '' }];
  
  // First check if text contains explicit $ delimiters
  const hasExplicitDelimiters = /\$[^\$]+\$/.test(text) || /\$\$[\s\S]+?\$\$/.test(text);
  
  // If no explicit delimiters but contains LaTeX commands, wrap the entire text
  if (!hasExplicitDelimiters && containsLatex(text)) {
    log.debug('Auto-wrapping LaTeX without delimiters:', text.substring(0, 100));
    return [{ type: 'inline', content: text }];
  }
  
  const parts = [];
  let remaining = text;
  
  // Regex patterns: $$...$$ for block, $...$ for inline
  const blockRegex = /\$\$([\s\S]+?)\$\$/;
  const inlineRegex = /\$([^\$\n]+?)\$/;
  
  while (remaining.length > 0) {
    const blockMatch = remaining.match(blockRegex);
    const inlineMatch = remaining.match(inlineRegex);
    
    // Find the first match
    let firstMatch = null;
    let matchType = null;
    
    if (blockMatch && (!inlineMatch || blockMatch.index <= inlineMatch.index)) {
      firstMatch = blockMatch;
      matchType = 'block';
    } else if (inlineMatch) {
      firstMatch = inlineMatch;
      matchType = 'inline';
    }
    
    if (!firstMatch) {
      parts.push({ type: 'text', content: remaining });
      break;
    }
    
    // Text before the match
    if (firstMatch.index > 0) {
      parts.push({ type: 'text', content: remaining.substring(0, firstMatch.index) });
    }
    
    // The LaTeX content
    parts.push({ type: matchType, content: firstMatch[1] });
    
    // Continue with the rest
    remaining = remaining.substring(firstMatch.index + firstMatch[0].length);
  }
  
  return parts;
}

/**
 * Normalize LaTeX escaping (LLMs often double-escape backslashes)
 */
function normalizeLatex(latex) {
  if (!latex) return '';
  // Convert \\frac to \frac, etc.
  return latex
    .replace(/\\\\([a-zA-Z]+)/g, '\\$1')
    .replace(/\\\\{/g, '\\{')
    .replace(/\\\\}/g, '\\}')
    .replace(/\\\\ /g, '\\ ');
}

/**
 * Render a single LaTeX expression
 */
function renderLatex(latex, displayMode = false) {
  const normalized = normalizeLatex(latex);
  
  if (katex) {
    try {
      const html = katex.renderToString(normalized, {
        displayMode,
        throwOnError: false,
        trust: true,
        strict: false
      });
      return <span dangerouslySetInnerHTML={{ __html: html }} />;
    } catch (error) {
      log.warn('KaTeX rendering error', error);
      // Fallback: show as styled code
      return (
        <code className="text-red-500 bg-red-50 dark:bg-red-900/20 px-1 rounded">
          {latex}
        </code>
      );
    }
  }
  
  // No KaTeX: show as styled monospace
  return (
    <code className="font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1 rounded">
      {latex}
    </code>
  );
}

/**
 * MathRenderer Component
 * Renders text with embedded LaTeX formulas
 */
export function MathRenderer({ text, className = '' }) {
  if (!text) return null;
  
  const parts = parseLatex(text);
  
  return (
    <span className={className}>
      {parts.map((part, i) => {
        if (part.type === 'block') {
          return (
            <div key={i} className="my-2 text-center overflow-x-auto">
              {renderLatex(part.content, true)}
            </div>
          );
        }
        if (part.type === 'inline') {
          return <span key={i}>{renderLatex(part.content, false)}</span>;
        }
        // Regular text - preserve whitespace and newlines
        return (
          <span key={i} style={{ whiteSpace: 'pre-wrap' }}>
            {part.content}
          </span>
        );
      })}
    </span>
  );
}

/**
 * BlockMath Component - for standalone math blocks
 */
export function BlockMath({ math, className = '' }) {
  return (
    <div className={`text-center my-4 overflow-x-auto ${className}`}>
      {renderLatex(math, true)}
    </div>
  );
}

/**
 * InlineMath Component - for inline math
 */
export function InlineMath({ math, className = '' }) {
  return <span className={className}>{renderLatex(math, false)}</span>;
}

/**
 * Enhanced CodeBlock Component
 * Supports both syntax-highlighted code AND LaTeX math rendering
 * 
 * Usage:
 * - language="python" → Syntax highlighting
 * - language="latex" or language="math" → LaTeX rendering
 * - language="text" → Plain text
 */
export function EnhancedCodeBlock({ language, code, className = '' }) {
  // If code is empty or only whitespace, don't render
  if (!code || code.trim() === '') {
    return null;
  }
  
  const lang = (language || 'text').toLowerCase();
  
  // LaTeX/Math mode: render as math
  if (lang === 'latex' || lang === 'math' || lang === 'katex') {
    return (
      <div className={`bg-gray-50 dark:bg-gray-800 rounded-lg p-4 my-2 overflow-x-auto ${className}`}>
        <BlockMath math={code} />
      </div>
    );
  }
  
  // Regular code: syntax highlighting
  return (
    <div className={`rounded overflow-hidden my-2 ${className}`}>
      <SyntaxHighlighter
        language={lang}
        style={vscDarkPlus}
        className="text-sm"
        customStyle={{ margin: 0, padding: '1rem' }}
        showLineNumbers={lang !== 'text'}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

/**
 * Detect if text contains LaTeX
 */
export function containsLatex(text) {
  if (!text) return false;
  
  // Check for explicit $ delimiters
  if (/\$[^\$]+\$/.test(text) || /\$\$[\s\S]+?\$\$/.test(text)) {
    return true;
  }
  
  // Check for common LaTeX commands without $ delimiters
  const latexCommands = /\\(frac|sqrt|sum|int|prod|lim|alpha|beta|gamma|delta|theta|pi|sigma|omega|partial|nabla|infty|cdot|times|pm|leq|geq|neq|approx|equiv|subset|supset|in|notin|cap|cup|emptyset|forall|exists|rightarrow|leftarrow|Rightarrow|Leftarrow|text|mathbb|mathbf|mathrm)/;
  const subscriptSuperscript = /[_^]\{/;
  
  return latexCommands.test(text) || subscriptSuperscript.test(text);
}

/**
 * Smart Text Renderer
 * Automatically detects and renders LaTeX if present, otherwise returns plain text
 */
export function SmartTextRenderer({ text, className = '' }) {
  if (!text) return null;
  
  // Log for debugging
  if (text.includes('$')) {
    log.debug('SmartTextRenderer detected LaTeX:', text.substring(0, 100));
  }
  
  if (containsLatex(text)) {
    return <MathRenderer text={text} className={className} />;
  }
  
  return <span className={className}>{text}</span>;
}

export default {
  MathRenderer,
  BlockMath,
  InlineMath,
  EnhancedCodeBlock,
  containsLatex,
  SmartTextRenderer
};
